import {Component, Inject, Mixins} from "vue-property-decorator";
import {QuotasCheckMixin} from "~/components/quotas-mixin";
import {MainContainer} from "~/components/main-container";
import {getUserDetails, getUsersManagementDependencies, removeUserAccount, saveUserAccount, toggleUserActivation} from "~/modules/users/service";
import {Loader} from "~/components/loader";
import {User} from "~/lib/models/user";
import {Columns} from "~/components/grid/columns";
import {Label} from "~/components/forms/label";
import {Column} from "~/components/grid/column";
import {XInput} from "~/components/forms/x-input";
import {Getter} from "vuex-class";
import {getClientRolesConfig} from "~/modules/client/service";
import XSelect from "~/components/forms/x-select";
import {isDefined} from "~/lib/helpers/guards";
import {className} from "~/lib/helpers/string";
import isEmail from 'validator/lib/isEmail';
import {Confirm} from "~/components/modals/confirm";
import {sort} from "~/lib/helpers";
import {Checkbox} from "~/components/checkbox";
import {formatDate} from "~/lib/helpers/formatters";
import {AppSettings} from "~/lib/app-settings";
import {getClientGroups, getClientTileSetEntities, getClientTileSets} from "~/modules/settings/services";
import {MultipleSelect} from "~/components/multiple-select";
import { StackSystem } from "~/components/stack-system";
import { TerritoryEditModal } from "~/components/modals/territory-edit-modal";

@Component
export class UsersEdit extends Mixins(QuotasCheckMixin) {

  @Inject() appSettings: AppSettings;
  @Inject() rootModalStack: StackSystem;
  @Getter currentUser;

  private isLoading = false;
  private rolesConfig: any = [];
  private isSaving = false;
  private isRemoving = false;
  private askRemoveConfirmation = false;
  private groups = [];
  private clientTileSets: {id: number; label: string[]}[] = [];
  private userTerritoryTileSetEntities: {geoCode: string; name: string}[] = [];
  private userCanByPassTerritoryReporting = false;

  private user: User = null;
  private originalUserRole = null;
  private isReadOnly:boolean = false;

  // Whether we ask to email the new user at creation
  private sendConfirmationMail = true;


  get inputUUID() {
    return this.$route.params.uuid;
  }

  get isCreateMode() {
    return this.inputUUID === "new";
  }

  get canEditRole() {
    if (this.isCreateMode) return true;
    return !this.user.hasPrivateRole;
  }

  // Check on roles assignations quotas
  get assignableRoles() {

    let roles = [];

    for(let config of this.rolesConfig ){
      if( isAssignable( config, this.originalUserRole)) {
        roles.push(config.role);
      }
    }

    return sort(roles, (a: any, b: any) => {
      if (a.label > b.label) return 1;
      if (a.label < b.label) return -1;
      return 0;
    });

  }

  get isValid() {
    if (this.canEditRole && !isDefined(this.user.role0)) return false;
    return !!this.user.email &&
      isEmail(this.user.email) &&
      !!this.user.profile.lastName &&
      !!this.user.profile.firstName &&
      !!this.user.profile.position;
  }

  async created() {

    this.isLoading = true;

    if (this.isCreateMode && !await this.checkMaxUsersQuota()) {
      return this.$router.replace("/users");
    }

    const [user, rolesConfig, groups, clientManagementDependencies, clientTileSets] = await Promise.all([
      this.isCreateMode ? User.create(this.appSettings.clientUUID) : getUserDetails(this.inputUUID),
      getClientRolesConfig(this.appSettings.clientUUID),
      getClientGroups(this.appSettings.clientUUID),
      getUsersManagementDependencies(this.appSettings.clientUUID),
      getClientTileSets(this.appSettings.clientUUID),
    ]);

    this.user = user;
    this.originalUserRole = user.role0;
    this.rolesConfig = rolesConfig;
    this.groups = groups;
    this.clientTileSets = clientTileSets;

    const scopeBypassTerritoryReporting = clientManagementDependencies.scopes.find(scope => scope.key === 'analysis:reporting:bypass-territory');
    const userRoleConfig = clientManagementDependencies.rolesConfig.find(roleConfig => roleConfig.role.id === this.user.role0);

    this.userCanByPassTerritoryReporting = !this.isCreateMode && typeof(scopeBypassTerritoryReporting) !== 'undefined' && userRoleConfig.role.scopes.includes(scopeBypassTerritoryReporting.id)
    await this.loadUserTerritoryTileSetEntities();

    this.isReadOnly = ! this.isCreateMode && this.user.hasPrivateRole;
    this.isLoading = false;
  }

  async loadUserTerritoryTileSetEntities(){
    this.userTerritoryTileSetEntities = (
      this.user.territory?.tsId && this.user.territory?.geoCodes?.length
      ? await getClientTileSetEntities(this.user.territory)
      : []
    );
  }

  async save() {

    if (this.isValid) {

      try {

        this.isSaving = true;

        this.user = await saveUserAccount(this.user, this.sendConfirmationMail);
        this.isSaving = false;

        this.$notify.success(this.isCreateMode ? "Compte utilisateur créé" : "Modifications enregistrées");

        if (this.isCreateMode) {
          return this.$router.push({name: "users-edit", params: {uuid: this.user.uuid}});
        }

      } catch (err) {
        if (err.message === "email registered") {
          this.$notify.error("Erreur lors de l'enregistrement: l'email est déjà utilisé");
        } else {
          this.$notify.error("Une erreur est survenue lors de l'enregistrement");
        }
        this.isSaving = false;
      }

    }
  }

  async toggleUserActivation() {
    this.user.active = !this.user.active;
    await toggleUserActivation(this.user.uuid);
  }

  async removeAccount() {
    this.isRemoving = true;
    await removeUserAccount(this.user.uuid);
    await this.$router.push({ name: "users", params: { clientUuid: this.appSettings.clientUUID }});
    this.isRemoving = false;
  }

  openTerritoryModal() {
    this.rootModalStack.push(destroy => (
      <TerritoryEditModal
        territory={this.user.territory}
        onCancel={() => destroy()}
        onCommit={async (newTerritory) => {
          this.user.territory = newTerritory;
          this.loadUserTerritoryTileSetEntities();
          destroy();
        }}
      />
    ));
  }

  renderTitle() {
    return this.isCreateMode ?
      <h2 class="page-title">Ajout d'un compte utilisateur</h2> :
      <h2 class="page-title">Edition d'un compte utilisateur</h2>;
  }

  renderBreadcrumb() {
    return (
      <nav class="breadcrumb" aria-label="breadcrumbs">
        <ul>
          <li>
            <router-link to={{ name: 'users', params: { clientUuid: this.appSettings.clientUUID } }}>Tous les utilisateurs</router-link>
          </li>
          <li class="is-active">
            <a href="#" aria-current="page">
              {this.isCreateMode ? "Nouveau compte" : this.user.email}
            </a>
          </li>
        </ul>
      </nav>
    );
  }

  renderRoleEdition() {

    if (this.isReadOnly) {
      return <XInput value="Interne" readonly/>;
    }

    if (this.canEditRole) {
      return (
        <XSelect
          allowNull
          nullLabel="-- Profils disponibles"
          value={this.user.role0}
          data={this.assignableRoles}
          label={role => role.label}
          equality={(r, userRoleId) => r?.id === userRoleId}
          onInput={role => this.user.role0 = role.id}
          readonly={this.assignableRoles.length === 0}
        />
      );
    }

    return <p class="is-small">Impossible d'éditer le profil utilisateur. Merci de contacter le support.</p>;
  }

  renderTerritory(){
    if (this.user.territory?.tsId && this.user.territory?.geoCodes?.length){
      return <div>
        <Label text={`Entités de la maille : ${(this.clientTileSets.find(tileSet => tileSet.id === this.user.territory?.tsId))?.label}`} />
        <div class="tags-list">
        {
          this.userTerritoryTileSetEntities.map(userTileSetEntity => {
            return <div class={"tag is-primary"}>
              {userTileSetEntity.name}
            </div>
          })
        }
        </div>
      </div>
    }
    else {
      return <Label text="Aucun territoire défini" />
    }
  }

  renderControls() {
    return (
      <div>

        {!this.isCreateMode && (
          <Columns>
            <Column>
              <h2 class="page-subtitle">Dernière connexion</h2>
              <div class="list">
                <div>
                  <div>{this.user.lastConnection ? formatDate(this.user.lastConnection, "le dd.MM.yyyy à HH:mm:ss") : "-"}</div>
                </div>
              </div>
            </Column>
            <Column>
              <h2 class="page-subtitle">Création du compte</h2>
              <div class="list">
                <div>
                  <div>{formatDate(this.user.createdAt, "dd.MM.yyyy")}</div>
                </div>
              </div>
            </Column>
          </Columns>
        )}

        <Columns>
          <Column>
            <Label text="Email"/>
            <XInput v-model={this.user.email} readonly={ this.isReadOnly || ! this.isCreateMode } />
          </Column>
          <Column>
            <Label text="Nom *"/>
            <XInput v-model={this.user.profile.lastName} readonly={this.isReadOnly}/>
          </Column>
          <Column>
            <Label text="Prénom *"/>
            <XInput v-model={this.user.profile.firstName} readonly={this.isReadOnly}/>
          </Column>
        </Columns>
        <Columns>
          <Column>
            <Label text="Profil utilisateur *"/>
            {this.renderRoleEdition()}
          </Column>
          <Column>
            <Label text="Fonction *"/>
            <XInput v-model={this.user.profile.position} readonly={this.isReadOnly}/>
          </Column>
          <Column>
            <Label text="Groupes"/>
            <MultipleSelect
              value={this.groups.filter( grp => {
                return this.user.groups.some( uGrpId => grp.id === uGrpId )
              })}
              nullLabel="--- Aucun groupe"
              dictionary={this.groups}
              labelFunction={v => v.label}
              multiple={true}
              equalsFunction={(group, dictObj) => group.id === dictObj.id}
              disabled={this.isReadOnly}
              onChange={groups => (this.user.updateGroupList(groups))}
            />
          </Column>
        </Columns>
        <div style="display:flex;align-items:start">
          <h2 class="page-subtitle">Territoire</h2>
          <p class="beta-flag">Beta</p>
          <button class="button is-small" style="margin-left:5px;margin-right:5px" onClick={() => this.openTerritoryModal()}>Modifier</button>
        </div>
        <Columns>
          <Column>
            <Label text={`Restrictiction de territoire : ${this.userCanByPassTerritoryReporting ? 'désactivée sur le profil utilisateur' : 'activée sur le profil utilisateur'}`} />
            {this.renderTerritory()}
          </Column>
        </Columns>
        <Columns>
          <Column>
          </Column>
        </Columns>

        { this.isCreateMode && (
          <Checkbox v-model={this.sendConfirmationMail}>
            Envoyer un email de confirmation à l'utilisateur
          </Checkbox>
        )}

      </div>
    );
  }

  renderContent() {

    if (this.isLoading) return <Loader/>;

    if (this.isCreateMode && this.assignableRoles.length === 0) {
      return [
        this.renderBreadcrumb(),
        <p>Impossible de créer un nouvel utilisateur, car aucun profil utilisateur n'est assignable</p>
      ];
    }

    return [
      <div class="users-edit_header">
        {this.renderBreadcrumb()}

        <div class="users-edit_header-controls">
          { (! this.isReadOnly && ! this.isCreateMode) && [
            // <button class="button is-small">Changer le mot de passe</button>,
            <button class="button is-small" onClick={this.toggleUserActivation}>
              {this.user.active ? "Desactiver le compte" : "Activer le compte"}
            </button>,
            <button class={className("button is-danger is-small", {isLoading: this.isRemoving})}
                    onClick={() => this.askRemoveConfirmation = true}>
              Supprimer le compte
            </button>
          ]}
        </div>
      </div>,
      this.renderControls(),
      ! this.isReadOnly && <div class="actions m-t-20">
        <button
          class={className("button is-primary", {isLoading: this.isSaving})}
          disabled={this.isValid === false}
          onClick={this.save}>
          Enregistrer
        </button>
      </div>
    ];
  }

  render() {
    return (
      <MainContainer>
        {this.renderTitle()}
        {this.renderContent()}

        <Confirm
          isActive={this.askRemoveConfirmation}
          type="danger"
          onClose={() => this.askRemoveConfirmation = false}
          onConfirm={this.removeAccount}
        >
          Vous êtes sur le point de supprimer le compte utilisateur. Veuillez confirmer la suppression.
        </Confirm>

      </MainContainer>
    );
  }
}

function isAssignable(config, originalUserRole) {
  if( config.role.id === originalUserRole?.id) return true;
  return config.maxUsers === -1 || config.meta.usersCount < config.maxUsers;
}

// VUE JSX HOT LOADER //
if (module.hot) require("/usr/src/app/node_modules/vue-jsx-hot-loader/src/api.js")({ Vue: require('vue'), ctx: eval('this'), module: module, hotId: "_vue_jsx_hot-169f29e1/users-edit.tsx" });